import { dehydrate } from '@tanstack/react-query';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';

import { CategoryDetail, ProductDetailsResponse, ProductsFilterItem, VirtualCategory } from '@api/catalog';
import { getCategoryDetail } from '@api/catalog/categories/api';
import { getProductFilters, getProducts, getProductsAuth } from '@api/catalog/product/api';
import { getInStockFilter } from '@api/catalog/product/catalogSearchParams';
import { getPagination } from '@api/common/helpers';
import { CommonResponse } from '@api/common/types';
import { getSeoTags } from '@api/seo/api';
import { SeoData } from '@api/seo/types';

import { HttpCode, ITEMS_PER_PRODUCTS_PAGE } from '@scripts/constants';
import {
    fromRoubleToKopecks,
    getSeoFilter,
    isNotEmptyObject,
    removeDefaultPaginationFromUrl,
    safeParseFilters,
} from '@scripts/helpers';
import { mergeProductsFilterResponses, splitLastFilter } from '@scripts/helpers/facets';
import { getCommonProps } from '@scripts/server/getCommonProps';
import { setAnonymousPageCacheHeaders } from '@scripts/server/pageCache';
import { ExtendedSSRDataProps } from '@scripts/server/types';

import SubCategory from './components/SubCategory';

const CategoryDetailPage = ({
    categoryId,
    total,
    dataProducts,
    seoData,
    filters,
    isNotFound,
    isError = false,
    categoryVirtualDetailData,
    categoriesVirtualDetailData,
}: {
    categoryId?: number;
    total?: number;
    dataProducts?: ProductDetailsResponse[];
    seoData?: SeoData;
    filters?: ProductsFilterItem;
    isNotFound?: boolean;
    isError?: boolean;
    categoryVirtualDetailData?: { data: VirtualCategory };
    categoriesVirtualDetailData?: { data: CategoryDetail };
}) => {
    return (
        <SubCategory
            categoryVirtualDetailData={categoryVirtualDetailData}
            categoriesVirtualDetailData={categoriesVirtualDetailData}
            categoryId={categoryId}
            totalSSR={total}
            dataProducts={dataProducts}
            seoData={seoData}
            filters={filters}
            isNotFound={isNotFound}
            isError={isError}
        />
    );
};

export default CategoryDetailPage;

export const getServerSideProps = async (
    context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<ExtendedSSRDataProps>> =>
    getCommonProps(context, async (props, queryClient, apiClient) => {
        const { params, query } = context;

        const urlFilters = { ...context.query };

        const isAuthUser = !!props.accessToken;

        // Anonymous catalog: short shared cache (nginx/CDN). Auth stays no-store for personalization.
        setAnonymousPageCacheHeaders(context.res, { isAuthenticated: isAuthUser });

        if (urlFilters && Number(urlFilters.page) < 0) {
            context.res.statusCode = HttpCode.NOT_FOUND;

            return { props: { ...props, isNotFound: true } };
        }

        if (query && query.page && query.page.includes('page')) {
            context.res.statusCode = HttpCode.NOT_FOUND;

            return { props: { ...props, isNotFound: true } };
        }

        if ((query && Number(query.page) === 0) || (query && Number(query.page) === 1)) {
            return {
                redirect: {
                    destination: removeDefaultPaginationFromUrl(
                        '/catalog' + (context.req.url as string).split('catalog')[1]
                    ),
                    statusCode: 301,
                },
            };
        }

        delete urlFilters.category;
        delete urlFilters.categories;
        delete urlFilters.page;

        const parsedFilters = safeParseFilters(urlFilters?.filters);

        const price_gte = parsedFilters?.price ? fromRoubleToKopecks(parsedFilters.price[0]) : null;
        const price_lte = parsedFilters?.price ? fromRoubleToKopecks(parsedFilters.price[1]) : null;

        const { price, isPreOrder, is_profitable, is_hit, ...restFilters } = parsedFilters;

        const preparedFilters = {
            ...restFilters,
            ...(price_gte && { price_gte }),
            ...(price_lte && { price_lte }),
            ...(isPreOrder === false && getInStockFilter()),
            ...(is_profitable === true && { is_profitable: true }),
            ...(is_hit === true && { is_hit: true }),
        };
        const { withoutLast: preparedFiltersWithoutLast, lastKey: lastFilterKey } = splitLastFilter(preparedFilters);

        try {
            const { key: categoryDetailKey, fetch: categoryDetailFetch } = getCategoryDetail(apiClient)({
                code: String(params?.category),
                ...(isNotEmptyObject(preparedFilters) && {
                    offers_filter: preparedFilters,
                }),
            });

            const { data }: CommonResponse<CategoryDetail> = await queryClient.fetchQuery({
                queryKey: categoryDetailKey,
                queryFn: categoryDetailFetch,
            });

            const { key: seoTagsKey, fetch: baseSeoTagsFetch } = getSeoTags(apiClient)(
                getSeoFilter(
                    `/catalog/${params && String(params.category)}${
                        context.query.page ? `?page=${context.query.page}` : ''
                    }`,
                    data.id
                )
            );

            const { key: filtersKey, fetch: filtersFetch } = getProductFilters(apiClient)({
                filter: {
                    category_id: Number(data.id),
                    ...preparedFilters,
                },
            });

            const { key: filtersLimitedKey, fetch: filtersLimitedFetch } = getProductFilters(apiClient)({
                filter: {
                    category_id: Number(data.id),
                    ...preparedFiltersWithoutLast,
                },
            });

            const { key: filtersBaseKey, fetch: filtersBaseFetch } = getProductFilters(apiClient)({
                filter: {
                    category_id: Number(data.id),
                },
            });

            // Card grid needs brand/category/images — skip heavy attributes/seller on SSR.
            const catalogProductInclude = ['brand', 'category', 'images'];

            const { key: baseProductsCategoryKey, fetch: baseProductsCategoryFetch } = getProducts(apiClient)({
                sort: 'availability_order',
                ...(data.id !== null && {
                    filter: {
                        category_id: data.id,
                        ...preparedFilters,
                    },
                }),
                pagination: getPagination(ITEMS_PER_PRODUCTS_PAGE, Number(context.query.page)),
                include: catalogProductInclude,
            });

            const { key: baseProductsAuthKey, fetch: baseProductsAuthFetch } = getProductsAuth(apiClient)({
                sort: 'availability_order',
                ...(data.id !== null && {
                    filter: {
                        category_id: data.id,
                        ...preparedFilters,
                    },
                }),
                pagination: getPagination(ITEMS_PER_PRODUCTS_PAGE, Number(context.query.page)),
                include: catalogProductInclude,
            });

            const [seoData, dataProducts, filtersResponse, filtersLimitedResponse, filtersBaseResponse] =
                await Promise.all([
                    queryClient.fetchQuery({
                        queryKey: seoTagsKey,
                        queryFn: baseSeoTagsFetch,
                    }),
                    queryClient.fetchQuery({
                        queryKey: isAuthUser ? baseProductsAuthKey : baseProductsCategoryKey,
                        queryFn: isAuthUser ? baseProductsAuthFetch : baseProductsCategoryFetch,
                    }),
                    queryClient.fetchQuery({
                        queryKey: filtersKey,
                        queryFn: filtersFetch,
                    }),
                    queryClient.fetchQuery({
                        queryKey: filtersLimitedKey,
                        queryFn: filtersLimitedFetch,
                    }),
                    queryClient.fetchQuery({
                        queryKey: filtersBaseKey,
                        queryFn: filtersBaseFetch,
                    }),
                ]);

            const filters = mergeProductsFilterResponses(
                filtersBaseResponse.data,
                filtersResponse.data,
                filtersLimitedResponse.data,
                lastFilterKey ? String(lastFilterKey) : undefined
            );

            if (!isAuthUser) {
                queryClient.setQueryData(baseProductsCategoryKey, dataProducts);
            }

            const paginationPage = dataProducts?.meta?.pagination;

            if (paginationPage && paginationPage.offset > paginationPage.total) {
                context.res.statusCode = HttpCode.NOT_FOUND;

                return { props: { ...props, isNotFound: true } };
            }

            if (data.children.length) {
                return {
                    props: {
                        dehydratedState: dehydrate(queryClient),
                        categoryId: data.id,
                        isLeafCategory: false,
                        seoData: {
                            data: {
                                ...seoData.data,
                                seo_text:
                                    (query && (Number(query.page) === 0 || Number(query.page) === 1)) ||
                                    (query && !query.page)
                                        ? seoData.data.seo_text
                                        : '',
                            },
                        },
                        dataCategory: data,
                        dataProducts,
                        filters,
                        ...props,
                    },
                };
            }

            return {
                props: {
                    dehydratedState: dehydrate(queryClient),
                    categoryId: data.id,
                    dataProducts,
                    dataCategory: data,
                    total: dataProducts?.meta?.pagination?.total || 0,
                    seoData: {
                        data: {
                            ...seoData.data,
                            seo_text:
                                (query && (Number(query.page) === 0 || Number(query.page) === 1)) ||
                                (query && !query.page)
                                    ? seoData.data.seo_text
                                    : '',
                        },
                    },
                    isLeafCategory: true,
                    filters,
                    ...props,
                },
            };
        } catch (error) {
            context.res.statusCode = HttpCode.NOT_FOUND;

            return { props: { ...props, isNotFound: true } };
        }
    });
